<template>
  <div class="space-y-4">
    <div class="flex flex-col gap-4">
      <LedgerEntrySelector
        v-if="selection === 'manual'"
        v-model="selectedLedgerEntries"
        amount-to-select-key="amountToPay"
        :scope="scope"
        :disabled="isAllocating"
        @update-total-amount="toRefund = $event / -100"
      />
      <div class="flex items-center justify-end gap-4">
        <div class="font-bold">
          {{
            t('refundAmount', {
              toRefund: formatCurrency(toRefund),
              paymentAmound: formatCurrency(payment.amount),
            })
          }}
        </div>
        <CVButton
          :disabled="!isValid"
          :is-loading="isAllocating"
          variant="default"
          @click="handleRefund"
        >
          {{ t('refund') }}
        </CVButton>
        <CVButton :disabled="isAllocating" variant="secondary" @click="$emit('cancel')">
          {{ t('cancel') }}
        </CVButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import CVButton from '@/components/buttons/CVButton.vue';
import type { Payment } from '@/entities/payments/payment.entity.ts';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useLedgerEntries } from '@/queries/use-ledger.ts';
import { useCreditWithRefund } from '@/queries/use-payment-api';
import { formatCurrency } from '@/utils/format-numbers.ts';
import { FindAllLedgerEntriesSort, Order } from '@carvia/ros-client-types';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LedgerEntrySelector from './LedgerEntrySelector.vue';
import { type BookingOrInvoiceScope, isBookingScope } from './utils/booking-or-invoice-scope';
import { Alert } from '@/services/alert.service';
import { sumBy } from 'lodash';

const props = withDefaults(
  defineProps<
    {
      payment: Payment;
      selection?: 'all' | 'manual';
    } & BookingOrInvoiceScope
  >(),
  {
    selection: 'all',
  },
);

const scope = computed(() =>
  isBookingScope(props)
    ? {
        bookingId: props.bookingId,
        counterpartyType: props.payment.counterpartyType,
      }
    : {
        invoiceId: props.invoiceId,
        counterpartyType: props.payment.counterpartyType,
      },
);

const { data: ledgerEntriesData } = useLedgerEntries(
  computed(() => ({
    bookingIds: isBookingScope(props) ? [props.bookingId] : undefined,
    invoiceIds: !isBookingScope(props) ? [props.invoiceId] : undefined,
    counterpartyTypes: props.payment.counterpartyType
      ? [props.payment.counterpartyType]
      : undefined,
    sort: [FindAllLedgerEntriesSort.DUE_DATE],
    order: Order.ASC,
    limit: 999,
  })),
);

const ledgerEntries = useFlattenPaginatedData(ledgerEntriesData);

const selectedLedgerEntries = ref<{ id: string; customAmount?: number }[]>(
  ledgerEntries.value.map(({ id }) => ({
    id,
  })),
);

watch(
  [ledgerEntries, () => props.selection],
  () => {
    if (props.selection === 'all') {
      selectedLedgerEntries.value = ledgerEntries.value.map(({ id }) => ({
        id,
      }));
    } else {
      selectedLedgerEntries.value = ledgerEntries.value
        .filter(({ amountToRequestPay }) => amountToRequestPay !== 0)
        .map(({ id }) => ({
          id,
        }));
    }
  },
  { immediate: true },
);

const isAllocating = ref(false);

const emit = defineEmits<{
  (e: 'success'): void;
  (e: 'cancel'): void;
}>();

const { t, locale } = useI18n();

const { mutateAsync: creditWithRefund } = useCreditWithRefund({
  locale,
});

const handleRefund = async () => {
  if (isAllocating.value) return;
  // Locked before the confirm dialog, so a second click cannot open another one and refund twice.
  isAllocating.value = true;
  try {
    const { isConfirmed } = await Alert.fire({
      titleText: t('confirmTitle'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('confirmButton'),
      cancelButtonText: t('cancel'),
    });
    if (isConfirmed) {
      await creditWithRefund({
        ledgerEntries: (selectedLedgerEntries.value ?? []).map(({ id, customAmount }) => ({
          id,
          customAmount: typeof customAmount === 'number' ? customAmount : undefined,
        })),
        paymentIds: [props.payment.id],
      });
      emit('success');
    } else {
      emit('cancel');
    }
  } finally {
    isAllocating.value = false;
  }
};

const toRefund = ref(0);

watch(
  [ledgerEntries, () => props.selection],
  () => {
    if (props.selection === 'all') {
      toRefund.value = sumBy(ledgerEntries.value, 'amountToPay') / -100;
    }
  },
  { immediate: true },
);

const isValid = computed(() => toRefund.value > 0 && toRefund.value <= props.payment.amount);
</script>

<i18n lang="json">
{
  "en": {
    "refundAmount": "Refund {toRefund} / {paymentAmound}",
    "refund": "Refund",
    "confirmTitle": "Really refund amount?",
    "confirmButton": "Erstatten"
  },
  "de": {
    "refundAmount": "{toRefund} / {paymentAmound} erstatten",
    "refund": "Erstatten",
    "confirmTitle": "Betrag wirklich erstatten?",
    "confirmButton": "Erstatten"
  },
  "es": {
    "refundAmount": "Reembolsar {toRefund} / {paymentAmound}",
    "refund": "Reembolsar",
    "confirmTitle": "¿Seguro que desea reembolsar el importe?",
    "confirmButton": "Reembolsar"
  }
}
</i18n>
